import React, { useState, useEffect, useContext, useRef } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";

import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import Select from "@material-ui/core/Select";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import Avatar from "@material-ui/core/Avatar";
import FormHelperText from "@material-ui/core/FormHelperText";
import Tooltip from "@material-ui/core/Tooltip";
import { makeStyles } from "@material-ui/core";

import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Autocomplete, {
	createFilterOptions,
} from "@material-ui/lab/Autocomplete";
import CircularProgress from "@material-ui/core/CircularProgress";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import toastError from "../../errors/toastError";
import useQueues from "../../hooks/useQueues";
import useWhatsApps from "../../hooks/useWhatsApps";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../Can";

const useStyles = makeStyles((theme) => ({
  maxWidth: {
    width: "100%",
  },
  noteField: {
    width: "100%",
    marginTop: 16,
    position: "relative",
  },
  quickAnswersWrapper: {
    position: "absolute",
    bottom: "100%",
    left: 0,
    right: 0,
    zIndex: 9999,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    maxHeight: 200,
    overflowY: "auto",
    boxShadow: theme.shadows[4],
    listStyle: "none",
    margin: 0,
    padding: "4px 0",
  },
  quickAnswersItem: {
    padding: "6px 12px",
    cursor: "pointer",
    fontSize: 13,
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  userNoQueueInfo: {
    display: "flex",
    alignItems: "flex-start",
    gap: 6,
    padding: "10px 12px",
    marginBottom: 16,
    borderRadius: 4,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.type === "dark" ? "rgba(30,136,229,0.08)" : "#e8f4fd",
    color: theme.palette.type === "dark" ? "#90caf9" : "#1565c0",
    fontSize: 13,
    lineHeight: 1.4,
  },
  disabledBtnWrapper: {
    display: "inline-block",
  },
}));

const filterOptions = createFilterOptions({
	trim: true,
});

// Funnel tanda 4: `onTransferred` OPCIONAL. Sin él (TicketOptionsMenu de
// /tickets) el modal navega a /tickets como siempre. Con él (chat flotante,
// ficha del Funnel) NO navega: el caller decide (cerrar el flotante, refrescar
// el tablero). Recibe { ticket, toUserName, toQueueName }.
const TransferTicketModal = ({ modalOpen, onClose, ticketid, ticketWhatsappId, onTransferred }) => {
	const history = useHistory();
	const [options, setOptions] = useState([]);
	const [allQueues, setAllQueues] = useState([]);
	const [filteredQueues, setFilteredQueues] = useState([]);
	const [loading, setLoading] = useState(false);
	const [searchParam, setSearchParam] = useState("");
	const [selectedUser, setSelectedUser] = useState(null);
	const [selectedQueue, setSelectedQueue] = useState("");
	const [selectedWhatsapp, setSelectedWhatsapp] = useState(ticketWhatsappId || "");
	const [userHasNoQueues, setUserHasNoQueues] = useState(false);
	const [noteText, setNoteText] = useState("");
	const [quickAnswers, setQuickAnswers] = useState([]);
	const [showQuickAnswers, setShowQuickAnswers] = useState(false);
	const noteRef = useRef(null);
	const classes = useStyles();
	const { findAll: findAllQueues } = useQueues();
	const { loadingWhatsapps, whatsApps } = useWhatsApps();
	const { user: loggedInUser } = useContext(AuthContext);

	const isTransferEnabled = selectedUser !== null || selectedQueue !== "";

	useEffect(() => {
		const loadQueues = async () => {
			const list = await findAllQueues();
			setAllQueues(list);
			setFilteredQueues(list);
		};
		loadQueues();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (!modalOpen) return;
		setLoading(true);
		const delayDebounceFn = setTimeout(() => {
			const fetchUsers = async () => {
				try {
					const { data } = await api.get("/users/", {
						params: { searchParam },
					});
					setOptions(data.users);
					setLoading(false);
				} catch (err) {
					setLoading(false);
					toastError(err);
				}
			};
			fetchUsers();
		}, searchParam.length === 0 ? 0 : 400);
		return () => clearTimeout(delayDebounceFn);
	}, [searchParam, modalOpen]);

	const handleClose = () => {
		onClose();
		setSearchParam("");
		setSelectedUser(null);
		setSelectedQueue("");
		setSelectedWhatsapp(ticketWhatsappId || "");
		setNoteText("");
		setUserHasNoQueues(false);
		setShowQuickAnswers(false);
		setQuickAnswers([]);
	};

	const handleUserChange = (newUser) => {
		setSelectedUser(newUser);
		if (newUser === null) {
			setFilteredQueues(allQueues);
			setUserHasNoQueues(false);
			return;
		}
		if (Array.isArray(newUser.queues) && newUser.queues.length > 0) {
			setFilteredQueues(newUser.queues);
			setSelectedQueue(newUser.queues[0].id);
			setUserHasNoQueues(false);
		} else {
			setFilteredQueues([]);
			setSelectedQueue("");
			setUserHasNoQueues(true);
		}
	};

	const handleNoteChange = (e) => {
		const val = e.target.value;
		setNoteText(val);
		handleLoadQuickAnswers(val);
	};

	const handleLoadQuickAnswers = async (val) => {
		if (val && val.indexOf("/") === 0) {
			try {
				const { data } = await api.get("/quickAnswers/", {
					params: { searchParam: val.substring(1) },
				});
				setQuickAnswers(data.quickAnswers || []);
				setShowQuickAnswers((data.quickAnswers || []).length > 0);
			} catch {
				setShowQuickAnswers(false);
			}
		} else {
			setShowQuickAnswers(false);
			setQuickAnswers([]);
		}
	};

	const handleQuickAnswerClick = (message) => {
		setNoteText(message);
		setShowQuickAnswers(false);
		setQuickAnswers([]);
		if (noteRef.current) noteRef.current.focus();
	};

	const handleSaveTicket = async (e) => {
		e.preventDefault();
		if (!ticketid) return;
		if (!isTransferEnabled) return;
		setLoading(true);
		try {
			const { data } = await api.post(`/tickets/${ticketid}/transfer`, {
				userId: selectedUser ? selectedUser.id : null,
				queueId: selectedQueue || null,
				whatsappId: selectedWhatsapp || null,
				note: noteText.trim() || null,
			});
			setLoading(false);
			toast.success(i18n.t("transferTicketModal.successToast"));
			const toUserName = selectedUser ? selectedUser.name : null;
			const toQueueName =
				(filteredQueues.find(q => q.id === selectedQueue) || allQueues.find(q => q.id === selectedQueue))?.name || null;
			handleClose();
			if (onTransferred) {
				onTransferred({ ticket: data, toUserName, toQueueName });
			} else {
				history.push("/tickets");
			}
		} catch (err) {
			setLoading(false);
			toastError(err);
		}
	};

	const transferBtn = (
		<ButtonWithSpinner
			variant="contained"
			color="primary"
			loading={loading}
			disabled={!isTransferEnabled || loading}
			onClick={handleSaveTicket}
		>
			{i18n.t("transferTicketModal.transferButton")}
		</ButtonWithSpinner>
	);

	return (
		<Dialog open={modalOpen} onClose={handleClose} maxWidth="sm" fullWidth scroll="paper">
			<form onSubmit={handleSaveTicket}>
				<DialogTitle id="form-dialog-title">
					{i18n.t("transferTicketModal.title")}
				</DialogTitle>
				<DialogContent dividers>
					{/* Campo usuario (opcional) */}
					<Autocomplete
						style={{ marginBottom: 16 }}
						getOptionLabel={(option) => option.name || ""}
						onChange={(e, newValue) => handleUserChange(newValue)}
						options={options}
						filterOptions={filterOptions}
						autoHighlight
						noOptionsText={i18n.t("transferTicketModal.noOptions")}
						loading={loading}
						renderOption={(option) => (
							<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
								<Avatar
									src={option.profilePicUrl}
									style={{ width: 28, height: 28, fontSize: 13 }}
								>
									{option.name?.charAt(0).toUpperCase()}
								</Avatar>
								<span>{option.name}</span>
							</div>
						)}
						renderInput={(params) => (
							<TextField
								{...params}
								label={i18n.t("transferTicketModal.fieldUser")}
								variant="outlined"
								autoFocus
								onChange={(e) => setSearchParam(e.target.value)}
								InputProps={{
									...params.InputProps,
									endAdornment: (
										<React.Fragment>
											{loading ? (
												<CircularProgress color="inherit" size={20} />
											) : null}
											{params.InputProps.endAdornment}
										</React.Fragment>
									),
								}}
							/>
						)}
					/>

					{/* Campo departamento: oculto si usuario no tiene queues */}
					{userHasNoQueues ? (
						<div className={classes.userNoQueueInfo}>
							<span>ℹ</span>
							<span>{i18n.t("transferTicketModal.userNoQueueInfo")}</span>
						</div>
					) : (
						<FormControl
							variant="outlined"
							className={classes.maxWidth}
							style={{ marginBottom: 16 }}
							required={!selectedUser}
						>
							<InputLabel>{i18n.t("transferTicketModal.fieldQueue")}</InputLabel>
							<Select
								value={selectedQueue}
								onChange={(e) => setSelectedQueue(e.target.value)}
								label={i18n.t("transferTicketModal.fieldQueue")}
							>
								<MenuItem value="">
									<em>{i18n.t("transferTicketModal.fieldQueuePlaceholder")}</em>
								</MenuItem>
								{filteredQueues.map((queue) => (
									<MenuItem key={queue.id} value={queue.id}>
										{queue.name}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					)}

					{/* Campo conexión (solo admin) */}
					<Can
						role={loggedInUser.profile}
						perform="ticket-options:transferWhatsapp"
						yes={() =>
							!loadingWhatsapps && (
								<FormControl
									variant="outlined"
									className={classes.maxWidth}
									style={{ marginBottom: 16 }}
									required
								>
									<InputLabel>{i18n.t("transferTicketModal.fieldConnection")}</InputLabel>
									<Select
										value={selectedWhatsapp}
										onChange={(e) => setSelectedWhatsapp(e.target.value)}
										label={i18n.t("transferTicketModal.fieldConnection")}
									>
										{whatsApps.map((wa) => (
											<MenuItem key={wa.id} value={wa.id}>
												{wa.name}
											</MenuItem>
										))}
									</Select>
								</FormControl>
							)
						}
					/>

					{/* Campo nota interna con Quick Messages */}
					<div className={classes.noteField}>
						{showQuickAnswers && (
							<ul className={classes.quickAnswersWrapper}>
								{quickAnswers.map((qa, idx) => (
									<li
										key={idx}
										className={classes.quickAnswersItem}
										onClick={() => handleQuickAnswerClick(qa.message)}
									>
										{qa.category && qa.category !== "General" && (
											<span style={{ fontSize: 10, color: "#667eea", marginRight: 4, fontWeight: 600 }}>
												[{qa.category}]
											</span>
										)}
										{`${qa.shortcut} - ${qa.message}`}
									</li>
								))}
							</ul>
						)}
						<TextField
							inputRef={noteRef}
							label={i18n.t("transferTicketModal.fieldNote")}
							placeholder={i18n.t("transferTicketModal.fieldNotePlaceholder")}
							multiline
							rows={3}
							variant="outlined"
							fullWidth
							value={noteText}
							onChange={handleNoteChange}
							helperText={i18n.t("transferTicketModal.fieldNoteHelper")}
						/>
					</div>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={handleClose}
						color="secondary"
						disabled={loading}
						variant="outlined"
					>
						{i18n.t("transferTicketModal.cancelButton")}
					</Button>
					{isTransferEnabled ? (
						transferBtn
					) : (
						<Tooltip title={i18n.t("transferTicketModal.disabledTooltip")} arrow>
							<span className={classes.disabledBtnWrapper}>
								{transferBtn}
							</span>
						</Tooltip>
					)}
				</DialogActions>
			</form>
		</Dialog>
	);
};

export default TransferTicketModal;
